import { CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { memo, useMemo } from 'react';

import { DEFAULT_LOADING_CONFIG, LOADING_ANIMATION, LOADING_ARIA, LOADING_SIZES } from '../constants';
import { LoadingSpinnerProps } from '../types';

/* eslint-disable react/prop-types */

/**
 * Modern loading spinner component using Material UI CircularProgress
 *
 * Features:
 * - Configurable size, color, and variant
 * - Theme integration
 * - Accessibility support
 * - Smooth animations
 * - Performance optimized with memoization
 */
const LoadingSpinner = memo<LoadingSpinnerProps>(
  ({
    size = DEFAULT_LOADING_CONFIG.SIZE,
    color = DEFAULT_LOADING_CONFIG.COLOR,
    variant = DEFAULT_LOADING_CONFIG.VARIANT,
    value,
    thickness = DEFAULT_LOADING_CONFIG.THICKNESS,
    disableShrink = DEFAULT_LOADING_CONFIG.DISABLE_SHRINK
  }) => {
    const theme = useTheme();

    // Calculate spinner size
    const spinnerSize = useMemo(() => {
      if (typeof size === 'number') return size;
      return LOADING_SIZES[size];
    }, [size]);

    // Determine if we should show progress value
    const showValue = variant === 'determinate' && typeof value === 'number';

    return (
      <CircularProgress
        size={spinnerSize}
        color={color}
        variant={variant}
        value={showValue ? value : undefined}
        thickness={thickness}
        disableShrink={disableShrink}
        aria-label={LOADING_ARIA.LABEL}
        role={LOADING_ARIA.ROLE}
        sx={{
          animationDuration: `${LOADING_ANIMATION.DURATION}ms`,
          // Ensure smooth animations
          transition: theme.transitions.create(['transform'], {
            duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut
          }),
          // Respect user's motion preferences
          '@media (prefers-reduced-motion: reduce)': {
            animationDuration: '0.01ms',
            animationIterationCount: 1
          }
        }}
      />
    );
  }
);

LoadingSpinner.displayName = 'LoadingSpinner';

export default LoadingSpinner;
